import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { Form } from "shared/@forms/Form";
import { FormActionBar } from "shared/@forms/FormActionBar";
import TextInput from "shared/@forms/TextInput";
import Button, { ButtonPurpose } from "shared/Button";
import CheckboxGroup from "shared/@forms/CheckboxGroup";
import styles from "./CategoryEdit.module.css";
import { TwitterPicker } from "react-color";
import classNames from "classnames";
import CategoryBanner from "../CategoryBanner";
import { RowSelector } from "../RowSelector/RowSelector";

export const defaultCategory = {
  id: null,
  parentCategoryID: null,
  title: "",
  subTitle: "",
  textColor: "#ffffff",
  backgroundColor: "#415364",
  isPublished: false,
  iconPath: null,
  rowNumber: 1,
  stepNumber: null,
  colorFill: false,
};

/**
 * Interactive component. Provides an edit form for Retail & Digital categories.
 */
export function CategoryEdit({
  category,
  onSubmit,
  onCancel,
  onCategoryChange,
  showPreview,
}) {
  const [_category, _setCategory] = useState(defaultCategory);

  // Update if category is changed from the outside.
  useEffect(() => {
    if (category) {
      _setCategory(category);
      onCategoryChange?.(category);
    }
  }, [category, onCategoryChange]);

  const patchCategory = useCallback(
    (patch) => {
      const updatedCategory = Object.assign({}, _category, patch);
      _setCategory(updatedCategory);
      onCategoryChange?.(updatedCategory);
    },
    [_category, onCategoryChange]
  );

  return (
    <div className={styles.container}>
      {showPreview && (
        <div className={styles.preview}>
          <div className={styles.previewGroup}>
            <label>Unselected</label>
            <CategoryBanner
              title={_category.title}
              textColor={_category.textColor}
              fillColor={_category.colorFill ? _category.backgroundColor : null}
            />
          </div>

          <div className={styles.previewGroup}>
            <label>Selected</label>
            <CategoryBanner
              title={_category.title}
              textColor={_category.textColor}
              fillColor={_category.backgroundColor}
            />
          </div>
        </div>
      )}
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({
            ..._category,
            isPublished: e.nativeEvent.submitter?.value !== "draft",
          });
        }}
      >
        <div className={styles.content}>
          <div className={classNames(styles.contentGroup, styles.textContent)}>
            <div className={styles.layoutGroup}>
              <div className={styles.inputGroup}>
                <label htmlFor="category-edit-name">Category Name</label>
                <TextInput
                  id="category-edit-name"
                  value={_category.title}
                  placeholder="Category Name"
                  required={true}
                  maxLength={30}
                  onChange={(e) => patchCategory({ title: e.target.value })}
                />
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="category-edit-row">Row</label>
                <div className={styles.row}>
                  <RowSelector
                    value={_category.rowNumber}
                    onChange={(rowNumber) => patchCategory({ rowNumber })}
                  />

                  <div className={styles.checkboxGroup}>
                    <CheckboxGroup
                      checked={_category.colorFill}
                      onChange={() => {
                        patchCategory({
                          colorFill: !_category.colorFill,
                        });
                      }}
                    >
                      Color fill
                    </CheckboxGroup>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.contentGroup}>
            <div className={styles.layoutGroup}>
              <div className={styles.inputGroup}>
                <label htmlFor="category-edit-bg-color">Background Color</label>
                <TwitterPicker
                  id="category-edit-bg-color"
                  onChange={(e) => patchCategory({ backgroundColor: e.hex })}
                  color={_category.backgroundColor}
                  triangle="hide"
                  width="350px"
                  colors={[
                    "#000000",
                    "#243746",
                    "#415364",
                    "#7f6a45",
                    "#9e703c",
                    "#91B6BB",
                    "#a89c96",
                    "#ADB7C0",
                    "#F5891E",
                    "#DB18C4",
                    "#5CD360",
                    "#2D8EF5",
                    "#FFFFFF",
                  ]}
                />
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="category-edit-text-color">Text Color</label>

                <TwitterPicker
                  id="category-edit-text-color"
                  onChange={(e) => patchCategory({ textColor: e.hex })}
                  color={_category.textColor}
                  triangle="hide"
                  width="350px"
                  colors={["#000000", "#FFFFFF"]}
                />
              </div>
            </div>
          </div>
        </div>

        <FormActionBar>
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onCancel();
            }}
          >
            Cancel
          </Button>
          <Button purpose={ButtonPurpose.save} type="submit">
            Save
          </Button>
          <Button type="submit" value="draft">
            Save as Draft
          </Button>
        </FormActionBar>
      </Form>
    </div>
  );
}

CategoryEdit.propTypes = {
  onCategoryChange: PropTypes.func,
  category: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    subTitle: PropTypes.string,
    textColor: PropTypes.string,
    backgroundColor: PropTypes.string,
  }).isRequired,

  onSubmit: PropTypes.func.isRequired,
};
